import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import React, {useCallback, useEffect, useState} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {PaymentInformation, RegisterOrganizationContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'
import {MemberJoinInfo, MemberJoinInfoForInputPage} from '../../../Props/MembersJoinPageStates'
import {AgreementCheckBoxContentBlock} from '../../Components/InputContentBlocks/AgreementCheckBoxContentBlock'
import {BasicMemberInfoConfirmContents} from '../../Components/ConfirmContentBlocks/BasicMemberInfoConfirmContents'
import {Nullable, Optional} from '../../../../Common/TypeHelper'
import {SearchPostCode} from '../../../../Common/SearchPostCode'
import {validateMemberJoinInput, ValidationJoinInput} from '../../../Utils/Validations/ValidateMembersJoinInput'
import {validateAgreeCheck} from '../../../Utils/Validations/MembersValidation'
import {useAsync, useAsyncRetry} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {MailMagazineSelectContentBlock} from '../../Components/InputContentBlocks/MailMagazineSelectContentBlock'
import {PaymentMethod} from '../../../DTO/PaymentMethodDTO'
import {PAYMENT} from '../../../../Common/Constants'
import {SelectContractTermContents} from '../../Components/PaymentsRelations/InputContentBlocks/SelectContractTermContents'
import {ContractInputInfo, ContractInputInfoAll, ContractMethod, ContractTerm, ErrorMessageForInvalidCard, ErrorMessagesBaseForContractInput, findAmountByTermAndMethod, findSecurityCodeByCardIdForValidation, getDefaultSecurityCodeMapForMyCreditCard, isFreeBucket, makeStringCreditCardExpireDate, MyCreditCardInfo, PaymentInputDetail, UsingMyCreditCardInfo} from '../../../Props/ContractInputInfo'
import {animateScroll} from 'react-scroll/modules'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {AlreadyJoinedContent} from './Components/AlreadyJoinedContent'
import {MembersPageAuthTemplate} from '../../Components/MembersPageAuthTemplate'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {MBLoadingOverlay} from '../../Molecules/MBLoadingOverlay'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {MBDeleteCreditCardConfirmDialog} from '../../Molecules/MBDialogs'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {PIMCard} from '../../Atoms/Cards/PIMCard'
import {MBContentSubTitleTypography} from '../../Atoms/Typographies'
import MemberAgreementRepo from '../../../Repo/MemberAgreementRepo'
import {MembersAgreement} from '../../Ecosystems/MembersAgreement'
import {MemberSignboardRepo} from '../../../Repo/MemberSignboardRepo'
import {JoinSelectPaymentMethodContents} from './Components/JoinSelectPaymentMethodContents'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {ErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/ErrorSummary'
import {memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {ContactAddressErrors, ContactAddressInputContents} from '../../Components/InputContentBlocks/AddressInputs/ContactAddressInputContents'
import {ContactAddress} from '../../../DTO/MemberAddressDTO'
import {JoinErrorCvsReadyContent} from './Components/JoinErrorCvsReadyContent'
import {MemberMailMagazine} from '../../../DTO/MemberMailMagazine'
import BucketHeader, {EnrollmentReasonInfo} from '../../../DTO/BucketHeader'
import {EnrollmentReasonSelectContentBlock, EnrollmentReasonSelectedInfo} from '../../Components/InputContentBlocks/EnrollmentReasonSelectContentBlock'
import {TakeoverAttentionSummary} from '../../Molecules/ErrorsAndAlert/AttentionSummary/TakeoverAttentionSummary'
import {SignedUpSignboard} from '../../../DTO/SignedUpSignboardDTO'
import {useTranslation} from 'react-i18next'

export type MembersJoinInputPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
  memberSignboardRepo: MemberSignboardRepo
  memberAgreementRepo: MemberAgreementRepo
  searchPostCode: SearchPostCode
}

type State = MemberJoinInfo

type ErrorState = {
  isInputError: boolean
  agreeCheckErrors: string[]
  postCodeErrors: string[]
  addressPrefectureCodeErrors: string[]
  addressCityErrors: string[]
  addressStreetErrors: string[]
  addressBuildingErrors: string[]
  phoneMobileErrors: string[]
  phoneTelErrors: string[]
  contractInputErrors: ErrorMessagesBaseForContractInput
  enrollmentReasonErrors: string[]
}

export type JoinInputPageLocationState = {
  isInvalidCardError: boolean
  isMobileCarrierError: boolean
  memberInfo: Nullable<MemberJoinInfoForInputPage>
  signboard: Nullable<SignedUpSignboard>
  paymentMethodList: Optional<PaymentMethod[]>
  contractInputInfo: ContractInputInfo
  myCreditCardInfoList: UsingMyCreditCardInfo[]
  agreedIdList: MemberJoinInfo['agreedIdList']
  mailMagazineIdCheckedList: MemberJoinInfo['mailMagazineIdCheckedList']
  enrollmentReasonSelectedInfo: MemberJoinInfo['enrollmentReasonSelectedInfo']
}

export const MembersJoinInputPage: React.VFC<MembersJoinInputPageProps> = (
  props,
): Nullable<JSX.Element> => {
  const {t} = useTranslation('member')
  const [openDialog, setOpenDialog] = useState(false)
  const [deleteCardInfo, setDeleteCardInfo] = useState<MyCreditCardInfo>()
  const [loadingOpen, setLoadingOpen] = useState(false)

  const {signupKey} = useParams<{ signupKey: string }>()
  const [processing, setProcessing] = useState<boolean>(false)

  const location = useLocation<JoinInputPageLocationState>()
  const locationState: JoinInputPageLocationState = location.state || {}

  const [signboard, setSignboard] = useState<Nullable<SignedUpSignboard>>(locationState?.signboard)
  const [state, setState] = useState<State>({
    mail: locationState.memberInfo?.mail || '',
    familyName: locationState.memberInfo?.familyName || '',
    firstName: locationState.memberInfo?.firstName || '',
    familyNameKana: locationState.memberInfo?.familyNameKana || '',
    firstNameKana: locationState.memberInfo?.firstNameKana || '',
    gender: locationState.memberInfo?.gender || '',
    birthday: locationState.memberInfo?.birthday || '',
    postCode: locationState.memberInfo?.postCode || '',
    addressPrefectureCode: locationState.memberInfo?.addressPrefectureCode || '',
    addressCity: locationState.memberInfo?.addressCity || '',
    addressStreet: locationState.memberInfo?.addressStreet || '',
    addressBuilding: locationState.memberInfo?.addressBuilding || '',
    phoneMobile: locationState.memberInfo?.phoneMobile || '',
    phoneTel: locationState.memberInfo?.phoneTel || '',
    agreedIdList: locationState.memberInfo?.agreedIdList || locationState.agreedIdList || [],
    mailMagazineIdCheckedList: locationState.memberInfo?.mailMagazineIdCheckedList ||
      locationState.mailMagazineIdCheckedList || [],
    enrollmentReasonSelectedInfo: locationState?.memberInfo?.enrollmentReasonSelectedInfo ||
      locationState.enrollmentReasonSelectedInfo,
  })
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    agreeCheckErrors:
      (locationState?.memberInfo?.agreedIdList !== undefined)
        ? validateAgreeCheck(locationState.memberInfo.agreedIdList)
        : [],
    postCodeErrors: [],
    addressPrefectureCodeErrors: [],
    addressCityErrors: [],
    addressStreetErrors: [],
    addressBuildingErrors: [],
    phoneMobileErrors: [],
    phoneTelErrors: [],
    contractInputErrors: {
      invalidCardErrors: ErrorMessageForInvalidCard(
        locationState?.contractInputInfo?.creditCard?.creditCardType,
        locationState?.isInvalidCardError,
      ),
      paymentInputTermErrors: [],
      paymentInputMethodErrors: [],
      creditCardTypeErrors: [],
      creditCardNumberErrors: [],
      creditCardExpireMonthErrors: [],
      creditCardExpireYearErrors: [],
      creditCardFutureExpireDateErrors: [],
      creditCardHolderNameErrors: [],
      creditCardSecurityCodeErrors: [],
      errorMyCreditCardId: (locationState?.isInvalidCardError
        && locationState?.contractInputInfo?.creditCard?.creditCardType === 'myCard')
        ? locationState?.contractInputInfo?.creditCard?.myCreditCardInfo.selectedCardId
        : '',
      myCreditCardSecurityCodeErrors: [],
      convenienceStoreErrors: [],
      mobileCarrierErrors: locationState?.isMobileCarrierError
        ? ['決済が完了しませんでした。再度お手続きを実施ください。']
        : [],
    },
    enrollmentReasonErrors: [],
  })

  const [showAgreement, setShowAgreement] = useState(false)

  const [paymentMethodList, setPaymentMethodList] =
    useState<PaymentMethod[]>(locationState?.paymentMethodList || [])
  const [myCreditCardInfoList, setMyCreditCardInfoList] =
    useState<UsingMyCreditCardInfo[]>(locationState?.myCreditCardInfoList || [])

  const [contractInputInfoState, setContractInputInfoState] = useState<ContractInputInfoAll>({
    term: locationState?.contractInputInfo?.term || '',
    method: locationState?.contractInputInfo?.method,
    amount: findAmountByTermAndMethod(
      locationState?.paymentMethodList,
      locationState?.contractInputInfo?.term,
      locationState?.contractInputInfo?.method,
    ),
    creditCard: {
      creditCardType: locationState?.contractInputInfo?.creditCard?.creditCardType || '',
      newCreditCardInfo: {
        cardNumber:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.cardNumber || '',
        expireMonth:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.expireMonth || '',
        expireYear:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.expireYear || '',
        holderName:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.holderName || '',
        securityCode:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.securityCode || '',
      },
      myCreditCardInfo: {
        selectedCardId:
          locationState?.contractInputInfo?.creditCard?.myCreditCardInfo.selectedCardId || '',
        securityCodeMap:
          locationState?.contractInputInfo?.creditCard?.myCreditCardInfo.securityCodeMap || [],
      },
    },
    mobileCarrier: {
      carrierKey: locationState?.contractInputInfo?.mobileCarrier?.carrierKey || '',
    },
    convenienceStore: {
      convenienceStoreKey:
        locationState?.contractInputInfo?.convenienceStore?.convenienceStoreKey || '',
    },
  })

  const throwMBMemberError = useMBMemberError()

  const getSelfResult = useAsync(async () => await props.memberRepo.getSelf())
  const getSelfCreditCardsResult = useAsyncRetry(async () =>
    await props.memberRepo.getSelfCreditCards())

  const handleAgreementButtonClick = useCallback((): void => {
    setShowAgreement(true)
  }, [])

  const handleAgree = useCallback((agreedIdList: number[]): void => {
    if (!signboard) throw new MBMemberError('SystemError')

    props.navigator.changeJoinInputPageState(
      {
        ...locationState.memberInfo,
        agreedIdList: agreedIdList,
      } as MemberJoinInfoForInputPage,
      signboard,
      locationState.paymentMethodList,
      locationState.myCreditCardInfoList,
      locationState.contractInputInfo,
    )
    setState((prevState) => {
      return {
        ...prevState,
        agreedIdList: agreedIdList,
      }
    })
    setErrorState((prevState) => {
      return {
        ...prevState,
        agreeCheckErrors: validateAgreeCheck(agreedIdList),
      }
    })
    setShowAgreement(false)
  }, [props.navigator, locationState, signboard])

  const handleDialogCancelButtonClick = useCallback((): void => {
    setOpenDialog(false)
  }, [openDialog])

  const handleDeleteCardButtonClick = useCallback((deleteCardInfo: MyCreditCardInfo): void => {
    setOpenDialog(true)
    setDeleteCardInfo(deleteCardInfo)
  }, [openDialog, deleteCardInfo])

  const handleDialogDeleteCardButtonClick = useCallback(async (cardId: string): Promise<void> => {
    if (!signboard) throw new MBMemberError('SystemError')

    setOpenDialog(false)
    setLoadingOpen(true)
    props.navigator.changeMembersJoinInputPageState(
      state,
      signboard,
      paymentMethodList,
      myCreditCardInfoList,
      contractInputInfoState,
    )

    try {
      const deleteCardResult = await props.memberRepo.deleteCreditCard(cardId)
      switch (deleteCardResult.status) {
        case CommonResponseStatus.Ok:
        case CommonResponseStatus.NotFound:
          setContractInputInfoState((prevState) => ({
            ...prevState,
            creditCard: {
              ...prevState.creditCard,
              creditCardType: '',
              myCreditCardInfo: {
                selectedCardId: '',
                securityCodeMap: prevState.creditCard.myCreditCardInfo.securityCodeMap,
              },
            },
          }))
          getSelfCreditCardsResult.retry()
          break
        case CommonResponseStatus.BadRequest:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        case CommonResponseStatus.InvalidRequest:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
      }

      setLoadingOpen(false)
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }, [
    props.navigator,
    props.memberRepo,
    getSelfCreditCardsResult,
    openDialog,
  ])

  const getPaymentMethodListBySignupKeyResult = useAsync(async () =>
    await props.memberRepo.getPaymentMethodListBySignupKey(signupKey))

  const getSignedUpSignboardsResult = useAsync(async () =>
    await props.memberSignboardRepo.getSignedUpSignboards())

  useEffect(() => {
    if (!getSelfResult.value) {
      return
    }
    const {member} = getSelfResult.value.data

    if (!getSelfCreditCardsResult.value) {
      return
    }
    setMyCreditCardInfoList(getSelfCreditCardsResult.value.data)

    if (
      locationState?.memberInfo &&
      locationState?.signboard &&
      locationState?.paymentMethodList &&
      locationState?.myCreditCardInfoList &&
      locationState?.contractInputInfo
    ) {
      return
    }

    if (!getPaymentMethodListBySignupKeyResult.value || !getSignedUpSignboardsResult.value) {
      return
    }

    const signboardDTO = getSignedUpSignboardsResult.value.data.find(s => s.signupKey === signupKey)
    if (!signboardDTO || !member) {
      throw new MBMemberError('SystemError')
    }

    setState((prevState) => ({
      ...prevState,
      mail: member.mail || '',
      familyName: member.familyName || '',
      firstName: member.firstName || '',
      familyNameKana: member.familyNameKana || '',
      firstNameKana: member.firstNameKana || '',
      gender: member.gender || '',
      birthday: member.birthday || '',
      postCode: member.postCode || '',
      addressPrefectureCode: member.addressPrefectureCode || '',
      addressCity: member.addressCity || '',
      addressStreet: member.addressStreet || '',
      addressBuilding: member.addressBuilding || '',
      phoneMobile: member.phoneMobile || '',
      phoneTel: member.phoneTel || '',
      mailMagazineIdCheckedList:
        locationState.mailMagazineIdCheckedList ||
        getDefaultMailMagazineIdCheckList(signboardDTO?.mailMagazineList || []),
      enrollmentReasonSelectedInfo: locationState.enrollmentReasonSelectedInfo ||
        getDefaultEnrollmentReasonSelectedInfo(signboardDTO?.mainService),
    }))

    setSignboard((prevState) => ({
      ...prevState,
      ...signboardDTO,
    }))

    const _paymentMethodList =
      getPaymentMethodListBySignupKeyResult.value?.data.map((item) => {
        return {...item}
      })
    setPaymentMethodList(_paymentMethodList)
    setContractInputInfoState((prevState) => (
      {
        ...prevState,
        term: isFreeBucket(_paymentMethodList)
          ? 'permanent'
          : (locationState?.contractInputInfo?.term || ''),
        method: isFreeBucket(_paymentMethodList)
          ? 'none'
          : locationState?.contractInputInfo?.method,
        amount: findAmountByTermAndMethod(
          _paymentMethodList,
          prevState.term,
          prevState.method,
        ),
      }
    ))
  }, [
    getSelfResult.value,
    getSelfCreditCardsResult.value,
    getSignedUpSignboardsResult.value,
    getPaymentMethodListBySignupKeyResult.value,
  ])

  useEffect(() => {
    if (locationState?.contractInputInfo && !locationState?.isMobileCarrierError) {
      return
    }

    setContractInputInfoState((prevState) => ({
      ...prevState,
      creditCard: {
        creditCardType: '',
        newCreditCardInfo: {
          cardNumber: '',
          expireMonth: '',
          expireYear: '',
          holderName: '',
          securityCode: '',
        },
        myCreditCardInfo: {
          selectedCardId: '',
          securityCodeMap: getDefaultSecurityCodeMapForMyCreditCard(myCreditCardInfoList),
        },
      },
    }))
  }, [myCreditCardInfoList])

  const goToJoinConfirmPage = useCallback((): void => {
    setProcessing(true)

    const getEnrollmentReasonListForValidate = (): EnrollmentReasonInfo[] => {
      if (signboard?.mainService?.isTakeover) return []
      return signboard?.mainService?.enrollmentReasonList || []
    }
    const inputForValidation: ValidationJoinInput = {
      ...state,
      agreedIdList: state.agreedIdList,
      postCode: state.postCode.trim(),
      addressCity: state.addressCity.trim(),
      addressStreet: state.addressStreet.trim(),
      addressBuilding: state.addressBuilding?.trim(),
      phoneMobile: state.phoneMobile?.trim(),
      phoneTel: state.phoneTel?.trim(),
      paymentInputTerm: contractInputInfoState?.term,
      paymentInputMethod: contractInputInfoState?.method,
      creditCardType: contractInputInfoState?.creditCard?.creditCardType || '',
      creditCardNumber:
        contractInputInfoState?.creditCard?.newCreditCardInfo.cardNumber || '',
      creditCardExpireMonth:
        contractInputInfoState?.creditCard?.newCreditCardInfo.expireMonth || '',
      creditCardExpireYear:
        contractInputInfoState?.creditCard?.newCreditCardInfo.expireYear || '',
      creditCardHolderName:
        contractInputInfoState?.creditCard?.newCreditCardInfo.holderName || '',
      creditCardSecurityCode:
        contractInputInfoState?.creditCard?.newCreditCardInfo.securityCode || '',
      myCreditCardSecurityCode: findSecurityCodeByCardIdForValidation(
        contractInputInfoState?.creditCard?.myCreditCardInfo,
      ) || '',
      convenienceStore: contractInputInfoState?.convenienceStore?.convenienceStoreKey || '',
      mobileCarrier: contractInputInfoState?.mobileCarrier?.carrierKey || '',
      enrollmentReasonValidateParam: {
        candidates: getEnrollmentReasonListForValidate(),
        selected: state.enrollmentReasonSelectedInfo,
      },
    }

    // noinspection JSUnusedLocalSymbols
    const {
      agreedIdList,
      postCode, addressCity, addressStreet, addressBuilding,
      phoneMobile, phoneTel,
    } = inputForValidation
    setState((prevState) => ({
      ...prevState,
      agreedIdList,
      postCode, addressCity, addressStreet, addressBuilding,
      phoneMobile, phoneTel,
    }))

    const validationResult = validateMemberJoinInput(inputForValidation)

    if (validationResult.hasError) {
      setProcessing(false)
      setErrorState({
        isInputError: true,
        agreeCheckErrors: validationResult.errors.agreedIdList,
        postCodeErrors: validationResult.errors.postCode,
        addressPrefectureCodeErrors: validationResult.errors.addressPrefectureCode,
        addressCityErrors: validationResult.errors.addressCity,
        addressStreetErrors: validationResult.errors.addressStreet,
        addressBuildingErrors: validationResult.errors.addressBuilding,
        phoneMobileErrors: validationResult.errors.phoneMobile,
        phoneTelErrors: validationResult.errors.phoneTel,
        contractInputErrors: {
          invalidCardErrors: [],
          paymentInputTermErrors: validationResult.errors.paymentInputTerm,
          paymentInputMethodErrors: validationResult.errors.paymentInputMethod,
          creditCardTypeErrors: validationResult.errors.creditCardType,
          creditCardNumberErrors: validationResult.errors.creditCardNumber,
          creditCardExpireMonthErrors: validationResult.errors.creditCardExpireMonth,
          creditCardExpireYearErrors: validationResult.errors.creditCardExpireYear,
          creditCardFutureExpireDateErrors: validationResult.errors.creditCardFutureExpireDate,
          creditCardHolderNameErrors: validationResult.errors.creditCardHolderName,
          creditCardSecurityCodeErrors: validationResult.errors.creditCardSecurityCode,
          errorMyCreditCardId: contractInputInfoState?.creditCard?.creditCardType === 'myCard'
            ? contractInputInfoState?.creditCard?.myCreditCardInfo?.selectedCardId
            : '',
          myCreditCardSecurityCodeErrors: validationResult.errors.myCreditCardSecurityCode,
          convenienceStoreErrors: validationResult.errors.convenienceStore,
          mobileCarrierErrors: validationResult.errors.mobileCarrier,
        },
        enrollmentReasonErrors: validationResult.errors.enrollmentReasonValidateParam,
      })
      animateScroll.scrollToTop()
      return
    }

    if (!signboard) throw new MBMemberError('SystemError')

    props.navigator.goToJoinConfirmPage(
      state,
      signboard,
      paymentMethodList,
      myCreditCardInfoList,
      contractInputInfoState,
    )
  }, [
    state,
    contractInputInfoState,
    signboard,
    props.navigator,
  ])

  const goToAccountTopPage = useCallback((): void => {
    props.navigator.goToAccountTopPage()
  }, [props.navigator])

  if (getSelfResult.loading) {
    return null
  }

  if (getSelfResult.error) {
    throw new MBMemberError(getSelfResult.error.message)
  }

  if (!getSelfResult.value) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(getSelfResult.value.status)

  if (getSignedUpSignboardsResult.loading) {
    return null
  } else if (getSignedUpSignboardsResult.error) {
    throw new MBMemberError(getSignedUpSignboardsResult.error.message)
  } else if (!getSignedUpSignboardsResult.value) {
    throw new MBMemberError('UnknownError')
  }
  memberResponseStatusCheck(getSignedUpSignboardsResult.value.status)

  const joinSignboard = getSignedUpSignboardsResult.value.data.find(s => s.signupKey === signupKey)
  if (joinSignboard === undefined) throw new MBMemberError('SystemError')
  if (joinSignboard.mainService.signedUp) {
    return (
      <AlreadyJoinedContent
        signboardName={joinSignboard.signupName}
        onClick={goToAccountTopPage}
      />
    )
  } else if (joinSignboard.signedUpStates.includes('convenience_store_ready')) {
    return (
      <JoinErrorCvsReadyContent
        signboard={joinSignboard}
        goToAccountTopPage={goToAccountTopPage}
        dataTestId={'join-error-cvs-ready'}
      />
    )
  }

  if (getSelfCreditCardsResult.loading) {
    return null
  }

  if (getSelfCreditCardsResult.error) {
    throw new MBMemberError(getSelfCreditCardsResult.error.message)
  }

  if (!getSelfCreditCardsResult.value) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(getSelfCreditCardsResult.value.status)

  if (getPaymentMethodListBySignupKeyResult.loading) {
    return null
  }

  if (getPaymentMethodListBySignupKeyResult.error) {
    throw new MBMemberError(getPaymentMethodListBySignupKeyResult.error.message)
  }

  if (!getPaymentMethodListBySignupKeyResult.value) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(getPaymentMethodListBySignupKeyResult.value.status)

  const {
    mail,
    familyName, firstName,
    familyNameKana, firstNameKana,
    gender,
    birthday,
    postCode,
    addressPrefectureCode, addressCity, addressStreet, addressBuilding,
    phoneMobile, phoneTel,
    agreedIdList,
    mailMagazineIdCheckedList,
    enrollmentReasonSelectedInfo,
  } = state

  const contactAddress: ContactAddress = {
    postCode: postCode,
    addressPrefectureCode: addressPrefectureCode,
    addressCity: addressCity,
    addressStreet: addressStreet,
    addressBuilding: addressBuilding,
    phoneMobile: phoneMobile,
    phoneTel: phoneTel,
  }

  const {
    postCodeErrors,
    addressPrefectureCodeErrors, addressCityErrors, addressStreetErrors, addressBuildingErrors,
    phoneMobileErrors, phoneTelErrors,
  }: ContactAddressErrors = errorState

  if (paymentMethodList.length === 0) {
    return null
  }

  const paymentInformationList: PaymentInformation[] =
    paymentMethodList
      .filter(paymentMethod =>
        Object.keys(PAYMENT.TERMS).includes(paymentMethod.term),
      )
      .map(paymentMethod => ({
        isFreeBucket: paymentMethod.method === 'none',
        termName: PAYMENT.TERMS[paymentMethod.term].displayLabel,
        contractAmount: paymentMethod.amount,
        order: PAYMENT.TERMS[paymentMethod.term].order,
        term: paymentMethod.term,
      }))

  if (paymentInformationList.length === 0) {
    throw new MBMemberError('SystemError')
  }

  if (!signboard) return null

  return (
    <>
      <MembersPageAuthTemplate hideFooterNavi={true}>
        {showAgreement ? (<div data-testid={'members-agreement-block'}>
          <MembersAgreement
            memberAgreementRepo={props.memberAgreementRepo}
            signboard={signboard}
            onAgreeClick={handleAgree}
            onDisagreeClick={handleAgree}
          />
        </div>) : (<div data-testid={'members-signup-input-block'}>
          <PageTitle>{`${signboard.signupName} 新規会員情報入力`}</PageTitle>

          <InputErrorSummary display={errorState.isInputError || locationState.isInvalidCardError}/>
          <ErrorSummary
            display={!errorState.isInputError && locationState.isMobileCarrierError}
            headline={'キャリア決済に失敗しました。'}
            detail={
              '入力項目をご確認の上、再実施いただくか、他の決済方法をご利用ください。\n また、何度もこのメッセージが表示される場合には、各キャリアへ問い合わせをお願いします。'
            }
          />
          <TakeoverAttentionSummary
            display={signboard.mainService.isTakeover}
            bucketMemberId={signboard.mainService.bucketMemberId}
          />

          <StepGuide activity={'signup'} current={'enter'}/>

          <PIMStack alignItems={'center'} spacing={'16px'}>
            <RegisterOrganizationContentBlock
              signboard={signboard}
              title={t('新規会員情報入力.入会バケット情報.見出し')}
              paymentInformationList={paymentInformationList}
            />


            <AgreementCheckBoxContentBlock
              agreedIdList={agreedIdList}
              agreeCheckErrors={errorState.agreeCheckErrors}
              onClick={handleAgreementButtonClick}
            />

            <BasicMemberInfoConfirmContents
              topMessage={[t('新規会員情報入力.基本情報.説明')]}
              mail={mail}
              familyName={familyName}
              firstName={firstName}
              familyNameKana={familyNameKana}
              firstNameKana={firstNameKana}
              gender={gender}
              birthday={birthday}
            />

            <PIMCard
              headerProps={{title: t('新規会員情報入力.連絡先情報.見出し')}}
              testId={'block-forms'}
            >
              <PIMStack
                spacing={'18px'}
                divider={<MBContentBodyDivider/>}
              >
                <MBContentSubTitleTypography>
                  連絡先情報に変更がある場合には、以下より修正してください。
                </MBContentSubTitleTypography>

                <ContactAddressInputContents
                  activity={'edit'}
                  searchPostCode={props.searchPostCode}
                  contactAddress={contactAddress}
                  contactAddressErrors={{
                    postCodeErrors,
                    addressPrefectureCodeErrors,
                    addressCityErrors,
                    addressStreetErrors,
                    addressBuildingErrors,
                    phoneMobileErrors,
                    phoneTelErrors,
                  }}
                  onChange={(address): void => {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        postCode: address.postCode,
                        addressPrefectureCode: address.addressPrefectureCode ?? '',
                        addressCity: address.addressCity ?? '',
                        addressStreet: address.addressStreet ?? '',
                        addressBuilding: address.addressBuilding ?? '',
                        phoneMobile: address.phoneMobile ?? '',
                        phoneTel: address.phoneTel ?? '',
                      }
                    })
                  }}
                />
              </PIMStack>
            </PIMCard>

            {(signboard.mailMagazineList?.length > 0) &&
                <MailMagazineSelectContentBlock
                    title={t('新規会員情報入力.メルマガ選択.見出し')}
                    topMessage={t('新規会員情報入力.メルマガ選択.説明')}
                    note={t('新規会員情報入力.メルマガ選択.補足')}
                    mailMagazineList={signboard.mailMagazineList}
                    mailMagazineIdCheckedList={mailMagazineIdCheckedList}
                    onChange={(checkedIdList: number[]): void => {
                      setState((prevState) => {
                        return {
                          ...prevState,
                          mailMagazineIdCheckedList: checkedIdList,
                        }
                      })
                    }}
                />
            }

            {!signboard.mainService.isTakeover && <EnrollmentReasonSelectContentBlock
                enrollmentReasonInfoList={signboard.mainService.enrollmentReasonList}
                enrollmentReasonSelectedInfo={enrollmentReasonSelectedInfo}
                onChange={(enrollmentReasonSelectedInfo): void => {
                  setState(prevState => (
                    {
                      ...prevState,
                      enrollmentReasonSelectedInfo,
                    }))
                }}
                errorMessages={errorState.enrollmentReasonErrors}
            />}

            {
              !isFreeBucket(getPaymentMethodListBySignupKeyResult.value.data) && <>
                    <SelectContractTermContents
                        paymentMethodList={paymentMethodList}
                        paymentInformationList={paymentInformationList}
                        contractInputInfoStateTerm={contractInputInfoState.term}
                        onChange={(changeSelectedTerm: ContractTerm): void => {
                          setContractInputInfoState((prevState) => {
                            return {
                              ...prevState,
                              term: changeSelectedTerm,
                              method: undefined,
                              amount: 0,
                            }
                          })
                        }}
                        errorMessage={errorState.contractInputErrors}
                    />

                    <JoinSelectPaymentMethodContents
                        paymentMethodList={paymentMethodList}
                        myCreditCardInfoList={myCreditCardInfoList}
                        contractInputInfoState={contractInputInfoState}
                        onChangeSelectedMethod={(
                          changeSelectedMethod: ContractMethod,
                        ): void => {
                          setContractInputInfoState((prevState) => {
                            return {
                              ...prevState,
                              method: changeSelectedMethod,
                              amount: findAmountByTermAndMethod(
                                paymentMethodList, prevState.term, changeSelectedMethod,
                              ),
                            }
                          })
                        }}
                        onChangeInputDetailInfo={(
                          changeInputDetailInfo: PaymentInputDetail,
                        ): void => {
                          setContractInputInfoState((prevState) => {
                            return {
                              ...prevState,
                              creditCard: changeInputDetailInfo.creditCard,
                              mobileCarrier: changeInputDetailInfo.mobileCarrier,
                              convenienceStore: changeInputDetailInfo.convenienceStore,
                            }
                          })
                        }}
                        errorMessages={errorState.contractInputErrors}
                        onDeleteCardButtonClick={handleDeleteCardButtonClick}
                    />
                </>
            }

          </PIMStack>


          <MessageForAction>上記の内容に間違いがなければ<br/>『会員情報の確認に進む』ボタンを押してください。</MessageForAction>

          <PIMPageActions>
            <PIMSubmitButton
              testId={'confirm-member-information-button'}
              disabled={processing}
              onClick={(): void => {
                goToJoinConfirmPage()
              }}
            >
              会員情報の確認に進む
            </PIMSubmitButton>
            <PIMSecondaryButton onClick={goToAccountTopPage}>
              {t('MYアカウント.ページ名')}に移動する
            </PIMSecondaryButton>
          </PIMPageActions>
        </div>)}
      </MembersPageAuthTemplate>

      <MBLoadingOverlay open={loadingOpen}/>
      {
        deleteCardInfo &&
          <MBDeleteCreditCardConfirmDialog
              open={openDialog}
              creditCardInfo={deleteCardInfo.cardNumber
                + makeStringCreditCardExpireDate(deleteCardInfo.expireDate)
              }
              onCancelButtonClick={handleDialogCancelButtonClick}
              onOkButtonClick={(): Promise<void> =>
                handleDialogDeleteCardButtonClick(deleteCardInfo.cardId)
              }
          />
      }
    </>
  )
}

const getDefaultMailMagazineIdCheckList = (mailMagazineList: MemberMailMagazine[]): number[] => {
  if (!mailMagazineList) return []

  return mailMagazineList
    .filter((mailMagazine) => mailMagazine.subscribed || mailMagazine.subscribed === null)
    .map(subscribedMailMagazine => subscribedMailMagazine.id)
}

const getDefaultEnrollmentReasonSelectedInfo = (
  mainService: Optional<BucketHeader>,
): Optional<EnrollmentReasonSelectedInfo> => {
  if (
    !mainService
    || !mainService.enrollmentReasonList
    || mainService.enrollmentReasonList.length === 0
  ) {
    return undefined
  }

  return {bucketId: mainService.id, enrollmentReasonId: undefined}
}
